/**
 * Courses Sidebar - Left sidebar for course list, Right sidebar for lesson list
 */
'use client';

import Link from 'next/link';
import { useState, useMemo } from 'react';
import { CertificateIcon, ChevronRightIcon, CompleteIcon, PlayVideoIcon } from '../Icons/Icons';
import Bookmark from '../common/bookmark';
import { useAuthUser } from '@/lib/auth/common/useSession';
import { getDuration, getTotalCourseDuration } from '@/lib/utils/duration';
import { CourseWithProgress } from '@/hooks/useUserLearningPaths';
import getContentType from '@/helpers/get-content-type';

interface CourseItem {
  title?: string;
  url: string;
  uid?: string;
  duration?: string;
  text_lesson?: boolean;
  time_to_read?: string;
  isBookmarked?: boolean;
  is_standalone?: boolean;
  video_lesson?: { duration?: number }[];
  taxonomies?: { taxonomy_uid?: string; parent_uid?: string; term_uid?: string; uid?: string }[];
  chapters?: {
    lessons?: {
      lessons?: {
        time_to_read?: string;
        video_lesson?: { duration?: number }[];
      }[];
    };
  }[];
  userProgress?: {
    isCompleted?: boolean;
    hasStarted?: boolean;
    percentage?: number;
    completedAt?: string | null;
  };
}

// User course data for bookmark state
interface UserCourseData {
  entryUid: string;
  isBookmarked?: boolean;
  progress?: {
    percentage?: number;
    isCompleted?: boolean;
  };
}
interface CoursesRightSidebarProps {
  url: string;
  title?: string;
  courses: CourseItem[];
  eyebrow?: string;
  courseUrl: string;
  userCoursesData?: UserCourseData[];
  /** When true, show Final Assessment item at bottom (learning path level) */
  showAssessment?: boolean;
  learningPath?: string;
  assessmentCurrentScore?: number | null;
}

const ListItem = ({
  item,
  i,
  isSelected,
  isCompleted,
  eyebrow,
  onCourseClick,
  icon,
  isLoggedIn = false,
  assessmentScore,
  showIncompleteIndicators = false,
}: {
  item: CourseItem | CourseWithProgress;
  i: number;
  isSelected: boolean;
  isCompleted: boolean;
  eyebrow?: string;
  onCourseClick?: (
    item: CourseItem | CourseWithProgress | null | undefined
  ) => void | undefined | null;
  icon?: React.ReactNode;
  isLoggedIn?: boolean;
  /** When set, show "Current Score: X%" below title (assessment item only). */
  assessmentScore?: number | null;
  /** When true, show red border and "Incomplete" for items with &lt;10% progress (only when Final Assessment is selected). */
  showIncompleteIndicators?: boolean;
}) => {
  const showIncomplete =
    showIncompleteIndicators &&
    isLoggedIn &&
    (item.userProgress?.percentage ?? 0) < 10 &&
    item.userProgress?.hasStarted;
  return (
    <li
      key={item.uid || item.title}
      onClick={() => onCourseClick?.(item)}
      className={`w-inherit hover:active hover:item-active mb-1 flex cursor-pointer flex-col transition-colors ${isSelected ? 'bg-gradient-tile-sheen border-l-2 border-[var(--Color-Extended-Amethyst-Amethyst-Accent)]' : 'bg-surface-1'} ${!isSelected && showIncomplete ? 'border-l-2 border-[var(--Surfaces-Extended-Red-Medium)]' : ''}`}
    >
      <div className="flex flex-row justify-between gap-4">
        <div className="w-[90%] px-2 py-3 pe-0 lg:px-6">
          <div className="cs-caption capitalize">
            {eyebrow ||
              ('text_lesson' in item && item.text_lesson ? 'Text Lesson' : 'Video Lesson')}
          </div>
          <h5 className="cs-h5">{item.title}</h5>

          {assessmentScore != null && (
            <p className="cs-caption mt-2">
              Current Score:{' '}
              <span className="font-semibold text-[var(--Color-Extended-Amethyst-Amethyst-Accent)]">
                {Math.ceil(assessmentScore)}%
              </span>
            </p>
          )}

          {isLoggedIn &&
            (item.userProgress?.isCompleted || (item.userProgress?.percentage ?? 0) > 0) && (
              <div className="flex flex-row items-center gap-2">
                <div className="flex h-1 w-[30%] flex-row bg-[var(--Surfaces-Overlay-Sheen-75)]">
                  <div
                    className={`h-1 ${item.userProgress?.isCompleted ? 'bg-[var(--Color-Extended-Green-Green-Shade)]' : 'bg-[var(--Surfaces-Extended-Amethyst-Transparent-Light)]'} transition-all duration-300`}
                    style={{
                      width: `${item.userProgress?.isCompleted ? '100' : item.userProgress?.percentage}%`,
                    }}
                  ></div>
                </div>
                {item.userProgress && item.userProgress.hasStarted ? (
                  <div className="cs-caption w-[50%]">
                    {item.userProgress?.isCompleted ? (
                      '100% Complete'
                    ) : (item.userProgress?.percentage ?? 0) >= 10 ? (
                      `${Math.ceil(item.userProgress?.percentage ?? 0)}% Complete`
                    ) : (item.userProgress?.percentage ?? 0) > 0 && showIncomplete ? (
                      <span className="cs-caption text-[var(--Surfaces-Extended-Red-Medium)]">
                        Incomplete
                      </span>
                    ) : (item.userProgress?.percentage ?? 0) > 0 ? (
                      `${Math.ceil(item.userProgress?.percentage ?? 0)}% Complete`
                    ) : null}
                  </div>
                ) : null}
              </div>
            )}
        </div>
        {icon && <div className="float-right self-center">{icon}</div>}

        <div className={`flex flex-row items-center px-1 ${isSelected ? 'bg-surface-3' : ''}`}>
          <ChevronRightIcon
            size={20}
            style={{
              color: isSelected ? 'var(--Text-Default-Strong)' : 'var(--Icons-Soft)',
            }}
          />
        </div>
      </div>
    </li>
  );
};

/** Left sidebar displaying course list with progress indicators */
export function CoursesLeftSidebar({
  courses,
  selectedCourse,
  onCourseClick,
  isAssessmentSelected = false,
  assessmentCurrentScore,
  showAssessment = false,
}: {
  courses: CourseItem[] | CourseWithProgress[];
  selectedCourse?: CourseItem | CourseWithProgress | null;
  onCourseClick?: (
    item: CourseItem | CourseWithProgress | null | undefined
  ) => void | undefined | null;
  isAssessmentSelected?: boolean;
  /** When assessment is completed, show this score below "Final Assessment" in the left item. */
  assessmentCurrentScore?: number | null;
  /** When true, show Final Assessment item (only for learning paths with is_assessment) */
  showAssessment?: boolean;
}) {
  // Reactive to login/logout (userDataUpdated/userLoggedOut events via SessionHydrator) —
  // a useState seeded once from isUserLoggedIn() only reflected auth state as of mount.
  const { isLoggedIn } = useAuthUser();

  return (
    <div
      className={`w-inherit h-[fill-content] overflow-y-scroll [scrollbar-width:none] [&::-webkit-scrollbar]:hidden`}
    >
      <ul>
        {courses.map((item: CourseItem, i: number) => {
          const isSelected = selectedCourse?.uid === item.uid;
          const isCompleted =
            (item as CourseWithProgress).isCompleted ??
            (item as CourseItem).userProgress?.isCompleted ??
            false;
          const contentTypeLabel =
            'taxonomies' in item && item.taxonomies?.length
              ? getContentType(item.taxonomies, 'content_type')
              : '';
          return (
            <ListItem
              key={item.uid || item.title}
              item={item}
              i={i}
              eyebrow={contentTypeLabel || 'Course'}
              isSelected={isSelected}
              isCompleted={isCompleted}
              onCourseClick={() => onCourseClick?.(item)}
              isLoggedIn={isLoggedIn}
              showIncompleteIndicators={isAssessmentSelected}
            />
          );
        })}

        {showAssessment && (
          <ListItem
            key={'final-item'}
            eyebrow="Assessment"
            item={{ title: 'Final Assessment', url: '#' }}
            i={courses.length + 1}
            isSelected={isAssessmentSelected}
            isCompleted={false}
            icon={
              <CertificateIcon
                className="text-[var(--Text-Default-Light)]"
                width={20}
                height={20}
                opacity={0.67}
              />
            }
            onCourseClick={() => onCourseClick?.(null)}
            isLoggedIn={isLoggedIn}
            assessmentScore={assessmentCurrentScore}
            showIncompleteIndicators={false}
          />
        )}
      </ul>
    </div>
  );
}

/** Right sidebar displaying expandable lesson list */
export function CoursesRightSidebar({
  url,
  title,
  courses,
  courseUrl,
  eyebrow,
  userCoursesData,
  showAssessment = false,
  learningPath,
  assessmentCurrentScore,
}: CoursesRightSidebarProps) {
  const [showMore, setShowMore] = useState<boolean>(false);
  // Reactive to login/logout (userDataUpdated/userLoggedOut events via SessionHydrator) —
  // a useState seeded once from isUserLoggedIn() only reflected auth state as of mount.
  const { isLoggedIn } = useAuthUser();

  // Merge courses with user data for bookmark state
  const coursesWithUserData = useMemo(() => {
    if (!userCoursesData || userCoursesData.length === 0) {
      return courses;
    }

    return courses.map((course) => {
      const userData = userCoursesData.find((u) => u.entryUid === course.uid);
      return {
        ...course,
        isBookmarked: userData?.isBookmarked ?? course.isBookmarked ?? false,
        userProgress: userData?.progress
          ? {
              ...course.userProgress,
              percentage: userData.progress.percentage,
              isCompleted: userData.progress.isCompleted,
            }
          : course.userProgress,
      };
    });
  }, [courses, userCoursesData]);

  // Calculate overall progress
  const overallProgress = useMemo(() => {
    if (!coursesWithUserData || coursesWithUserData.length === 0) return 0;
    const totalProgress = coursesWithUserData.reduce((sum, course) => {
      return sum + (course.userProgress?.percentage ?? 0);
    }, 0);
    return Math.ceil(totalProgress / coursesWithUserData.length);
  }, [coursesWithUserData]);

  return (
    <>
      <div className="mb-5 w-full">
        <div className="bg-gradient-sheen py-4 ps-[22px] pe-6">
          <h6 className="cs-h6 text-[var(--Text-Default-Light)]">{eyebrow}</h6>
          <h4 className="cs-h4 mb-1 text-[var(--Text-Default-Medium)]">{title || 'Course'}</h4>
          {isLoggedIn && (
            <div className="bg-surface-5 h-1 rounded-full">
              <div
                className={`h-1 rounded-full transition-all duration-300 ${
                  overallProgress >= 100
                    ? 'bg-[var(--Color-Extended-Green-Green-Shade)]'
                    : 'bg-[var(--Color-Extended-Amethyst-Amethyst-Accent)]'
                }`}
                style={{ width: `${overallProgress}%` }}
              ></div>
            </div>
          )}
          <div className="cs-caption mt-1">
            {isLoggedIn && (
              <>
                {eyebrow}:{' '}
                <span
                  className={
                    overallProgress >= 100
                      ? 'text-[var(--Color-Extended-Green-Green-Shade)]'
                      : 'text-[var(--Color-Extended-Amethyst-Amethyst-Accent)]'
                  }
                >
                  {overallProgress}%
                </span>{' '}
                complete
              </>
            )}
            <button
              className="text-text-action-medium hover:text-text-action-strong float-right mt-[-1%]"
              onClick={() => setShowMore(!showMore)}
            >
              {showMore ? 'Show less' : 'Show more'}
            </button>
          </div>
        </div>

        {showMore && (
          <div
            className={`max-h-[500px] w-full overflow-y-scroll [scrollbar-width:none] [&::-webkit-scrollbar]:hidden ${showMore ? 'max-h-[600px] opacity-100' : 'max-h-0 opacity-0'} `}
          >
            <ul>
              {coursesWithUserData.map((item: CourseItem) => {
                const isCurrentCourse = item.url === courseUrl || item.url === `/${courseUrl}`;
                return (
                  <li
                    key={item.title}
                    className={`group relative flex w-full flex-col hover:bg-[var(--Surfaces-Extended-Amethyst-Transparent-Soft)] ${isCurrentCourse ? 'bg-gradient-tile-sheen' : 'bg-surface-1'}`}
                  >
                    <Link href={`${url}${item.url}`} prefetch={true} className="py-3 lg:px-6">
                      <div className="flex flex-row items-center justify-between gap-4">
                        <div className="w-[80%]">
                          <h6 className="cs-h6">{item.title}</h6>
                          <div className="flex flex-row items-center gap-2">
                            <div className="cs-caption capitalize">
                              {'taxonomies' in item && item.taxonomies?.length
                                ? getContentType(item.taxonomies, 'content_type') || 'Course'
                                : 'Course'}
                            </div>
                            <div>
                              <span className="divider" />
                            </div>
                            <div className="cs-caption">
                              {item?.time_to_read ||
                                getTotalCourseDuration(item) ||
                                (item?.duration ? getDuration(item.duration) : null) ||
                                'Duration not available'}
                            </div>
                          </div>
                        </div>

                        <div className="relative z-10 flex h-6 w-[60px] flex-row items-center justify-end">
                          {/* Permanent icons layer - hide on hover */}
                          <div className="absolute top-0 right-0 flex h-full items-center gap-1 transition-opacity duration-200 group-hover:opacity-0">
                            {item?.isBookmarked && (
                              <Bookmark isBookmarkedBool={true} courseEntryUid={item.uid} />
                            )}

                            {item?.userProgress?.isCompleted && (
                              <CompleteIcon className="text-[var(--Icons-Medium)]" />
                            )}
                          </div>

                          {/* Hover icons layer - fades in on hover */}
                          <div className="absolute top-0 right-0 flex h-full items-center gap-1 opacity-0 transition-opacity duration-200 group-hover:opacity-100">
                            <Bookmark
                              isBookmarkedBool={item?.isBookmarked ?? false}
                              courseEntryUid={item.uid}
                            />

                            {item?.userProgress?.isCompleted && (
                              <CompleteIcon className="text-[var(--Icons-Medium)]" />
                            )}

                            {!item?.userProgress?.isCompleted && (
                              <div className="flex items-center justify-center">
                                <ChevronRightIcon
                                  size={20}
                                  style={{
                                    color: 'var(--Icons-Medium)',
                                  }}
                                />
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    </Link>

                    {isLoggedIn &&
                      (item.userProgress?.isCompleted ||
                        (item.userProgress?.percentage ?? 0) > 0) && (
                        <div className="absolute bottom-0 left-0 flex h-1 w-full bg-[var(--Surfaces-Overlay-Sheen-75)]">
                          <div
                            className={`h-1 ${item.userProgress?.isCompleted ? 'bg-[var(--Color-Extended-Green-Green-Shade)]' : 'bg-[var(--Surfaces-Extended-Amethyst-Transparent-Light)]'} transition-all duration-300`}
                            style={{
                              width: `${item.userProgress?.isCompleted ? 100 : item.userProgress?.percentage}%`,
                            }}
                          ></div>
                        </div>
                      )}
                  </li>
                );
              })}
              {showAssessment && learningPath && (
                <li className="group relative flex w-full flex-col hover:bg-[var(--Surfaces-Extended-Amethyst-Transparent-Soft)] bg-surface-1">
                  <Link
                    href={`/learning-paths/${learningPath}/assessment`}
                    prefetch={true}
                    className="flex flex-row items-center justify-between gap-4 py-3 lg:px-6"
                  >
                    <div className="w-[80%]">
                      <div className="flex items-center gap-2">
                        <CertificateIcon className="text-[var(--Icons-Medium)]" />
                        <h6 className="cs-h6">Assessment</h6>
                      </div>
                      <div className="cs-caption text-text-default-subtle mt-1">
                        Final Assessment
                        {assessmentCurrentScore != null && (
                          <span className="ml-1 font-semibold text-[var(--Color-Extended-Amethyst-Amethyst-Accent)]">
                            · {Math.ceil(assessmentCurrentScore)}%
                          </span>
                        )}
                      </div>
                    </div>
                    <ChevronRightIcon size={20} style={{ color: 'var(--Icons-Soft)' }} />
                  </Link>
                </li>
              )}
            </ul>
          </div>
        )}
      </div>
    </>
  );
}
